import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Email sau parolă incorectă';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await api.post('/auth/register', userData);
      const { access_token, user: userInfo } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userInfo));
      setUser(userInfo);
      return { success: true, user: userInfo };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Eroare la înregistrare';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Google sign-in gestionat de Emergent: schimbăm session_id pe JWT-ul aplicației
  const loginWithGoogleSession = React.useCallback(async (sessionId) => {
    try {
      setError(null);
      const response = await api.post('/auth/google/session', { session_id: sessionId }, {
        headers: { 'X-Session-ID': sessionId },
        withCredentials: true,
      });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Autentificarea cu Google a eșuat.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const logout = () => {
    try {
      api.post('/auth/google/logout', {}, { withCredentials: true }).catch(() => {});
    } catch (e) { /* ignorăm */ }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (newData) => {    setUser((prev) => {
      const merged = { ...(prev || {}), ...newData };
      localStorage.setItem('user', JSON.stringify(merged));
      return merged;
    });
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    const hasUser = !!user;
    
    // If no token, definitely not authenticated
    if (!token) {
      return false;
    }
    
    // If has token but no user in state, try to load from localStorage
    if (!hasUser) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          return true;
        } catch (e) {
          // Invalid user data, clear everything
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          return false;
        }
      }
      return false;
    }
    
    return true;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, updateUser, login, register, loginWithGoogleSession, logout, loading, error, isAdmin, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}